"use client";

import Title from "@/components/Title";
import Button from "@/components/Button";
import NewsletterSignup from "@/components/NewsletterSignup";
import { CommonStoryblok, FooterStoryblok, ButtonStoryblok } from "@/component-types-sb";
import { storyblokEditable } from "@storyblok/react";
import IconBlock from "@/components/IconBlock";
import Link from "next/link";
import Image from "next/image";
import { getStoryblokImageUrl } from "@/lib/storyblok-image";
import OpeningHours from "@/components/OpeningHours";

export default function Footer({ footerData, commonData }: Readonly<{ footerData: FooterStoryblok, commonData: CommonStoryblok  }>) {
  return (
    <footer {...storyblokEditable(footerData)} className="bg-card-muted text-white m-6 lg:mx-16 -mt-10 p-6 lg:p-16 rounded-lg">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="space-y-8">
            <div className="space-y-2">
              <Title variant="h2">&quot;{footerData?.title}&quot;</Title>
            </div>

            <div className="flex space-x-6">
              {footerData?.social_buttons &&
                footerData.social_buttons.map((entry: ButtonStoryblok) => (
                  <Button key={entry._uid} blok={entry} />
                ))}
            </div>

            <Button
              blok={{
                component: "button",
                _uid: "reserve-button",
                title: "Jetzt reservieren",
                variant: "primary",
                type: "reserve",
              }}
            />
          </div>

          <div className="space-y-6">
            <Title variant="h3">Öffnungszeiten</Title>

            <OpeningHours
              entries={commonData?.opening_hours}
              className="flex flex-col gap-2"
            />
          </div>

          <div className="space-y-8">
            <div>
              <Title variant="h3" className="mb-6">
                Nichts verpassen
              </Title>
              <p className="mb-4">Melde dich für unseren Newsletter an.</p>

              <div className="flex max-w-md">
                <NewsletterSignup />
              </div>
            </div>

            <div>
              <Title variant="h3" className="mb-6">
                Hauptsponsor
              </Title>
              {footerData?.main_sponsor_image?.filename && (
                <Link
                  href={footerData?.main_sponsor_link?.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Hauptsponsor"
                >
                  <Image
                    src={getStoryblokImageUrl(footerData?.main_sponsor_image?.filename, "200x0")}
                    alt={footerData?.main_sponsor_image?.alt || "Hauptsponsor"}
                    width={200}
                    height={48}
                    loading="lazy"
                    unoptimized
                  />
                </Link>
              )}
            </div>
          </div>

          {footerData?.icon_blocks && footerData.icon_blocks.length > 0 && (
            <>
              <div className="border-t-1 border-white md:col-span-3" />
              <div className="md:col-span-3 flex flex-col items-start w-fit mx-auto md:grid md:grid-cols-2 md:justify-items-start xl:grid-cols-4 xl:w-full xl:justify-items-center gap-2">
                {footerData.icon_blocks.map((entry) => (
                  <IconBlock
                    key={entry._uid}
                    blok={entry}
                    className="md:min-w-2xs"
                  />
                ))}
              </div>
            </>
          )}

          <div className="border-t-1 border-white pt-8 md:col-span-3 text-center">
            {footerData?.links &&
              footerData.links.map((entry: ButtonStoryblok) => (
                <Button 
                  className="text-white"
                  key={entry._uid}
                  blok={entry}
                />
              ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
